import React from 'react';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import { AdminContext } from './AdminContext';
import { DatabaseContext } from '../database/DatabaseHandler';
import JsonEditor from './JsonEditor';
import AdminApi from './AdminApi';
import PrestaGlobals from '../common/PrestaGlobals';
import { valueToStored, formatParamRef } from './paramUtils';

/**
 * EA-444: edit a parameter's descriptor + row properties as raw JSON. Opened by right-clicking a
 * visible card or via the Edit button in the Paraméterek tab (for hidden params).
 *  - "Alkalmaz" → in-memory live preview (updateScripts).
 *  - "Mentés (adatbázis)" → persists the DEFINITION columns via updateParamDef, then reloads.
 * The `id` is preserved; the identity (parameterName/property/type) is NOT persisted here.
 */
const DescriptorEditorDialog: React.FC = () => {
    const admin = React.useContext(AdminContext);
    const data = React.useContext(DatabaseContext);
    const param = admin.editingParam;
    const [status, setStatus] = React.useState<string | null>(null);

    React.useEffect(() => { setStatus(null); }, [param]);

    const handleApply = (parsed: any) => {
        if (!param) return;
        const next = (data.paramScripts ?? []).map(p =>
            p.id === param.id ? { ...parsed, id: param.id } : p
        );
        data.updateScripts(next, data.uconScript);
        // Refresh the editor baseline to the applied value (keeps the dialog open, clears "dirty").
        admin.openDescriptorEditor({ ...parsed, id: param.id });
    };

    const handleSave = async (text: string) => {
        if (!param) return;
        let parsed: any;
        try { parsed = JSON.parse(text); } catch (e: any) { setStatus('Érvénytelen JSON: ' + (e?.message ?? '')); return; }

        // 1) Definition (global) → uc_style_product_parameters
        // targetPath null vs "" are distinct (no Path key vs empty Path). Send target_path_null so the
        // backend can persist the difference instead of collapsing "" → NULL.
        const isNullPath = parsed.targetPath == null; // null/undefined → NULL; "" stays ""
        const defRes = await AdminApi.updateParamDef(param.id, PrestaGlobals.productId, {
            descriptor: JSON.stringify(parsed.typeDescriptor ?? {}),
            target_path: isNullPath ? '' : String(parsed.targetPath),
            target_path_null: isNullPath ? '1' : '0',
            target_type: parsed.targetType ?? '',
            tab: parsed.tab ?? 'configuration',
            title: parsed.title ?? '',
            panel: parsed.panel ?? '',
            sort_index: parsed.sortIndex != null ? String(parsed.sortIndex) : '',
        });
        const defOk = !!(defRes && 'success' in defRes && defRes.success);

        // 2) Value (per shop) → uc_style_parameter_values. The dialog shows parameterValue, so persist it too.
        let valOk = true;
        const valueStr = valueToStored(parsed.parameterValue);
        if (valueStr !== '') {
            const valRes = await AdminApi.saveParamValue(param.id, PrestaGlobals.productId, valueStr);
            valOk = !!(valRes && valRes.success);
        }

        if (defOk && valOk) {
            setStatus('Mentve (definíció + érték a bolthoz).');
            data.reloadScripts();
        } else {
            setStatus('Mentés ' + (!defOk ? 'definíció' : 'érték') + ' hiba: ' +
                (defRes && 'error' in defRes ? (defRes as any).error : 'ismeretlen'));
        }
    };

    return (
        <Dialog
            open={!!param}
            onClose={() => admin.closeDescriptorEditor()}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                Paraméter szerkesztése
                {param && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {formatParamRef(param)}
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Nyers JSON. Az „Alkalmaz” élő előnézet; a „Mentés (adatbázis)” menti a leírót és
                    tulajdonságokat (<code>targetPath</code>, <code>targetType</code>, <code>tab</code>,
                    <code>title</code>, <code>panel</code>, <code>sortIndex</code>, <code>typeDescriptor</code>)
                    globálisan, ÉS a <code>parameterValue</code>-t a bolthoz. A paraméter neve/property/típusa
                    innen NEM módosul (ahhoz törlés + újra létrehozás).
                </Typography>
                {param && (
                    <JsonEditor
                        value={param}
                        applyLabel="Alkalmaz (élő előnézet)"
                        dirtyNote="Nem mentett változás — élő előnézet; mentéshez „Mentés (adatbázis)”."
                        onApply={handleApply}
                        renderExtraActions={(text, valid) => (
                            <Button variant="outlined" size="small" color="primary" disabled={!valid}
                                onClick={() => handleSave(text)}>
                                Mentés (adatbázis)
                            </Button>
                        )}
                    />
                )}
                {status && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {status}
                    </Typography>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default DescriptorEditorDialog;
